import { ArrowRight, ChevronDown, Globe2, Sparkles } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { buildAppLoginUrl, navigateToApp } from "@/auth/authLinks";
import { bootstrapAuthSession, getLandingIdentity } from "@/auth/authSession";
import { content } from "@/components/landing/content";
import { InviteCodeDialog } from "@/components/air/InviteCodeDialog";
import { setLandingBrandUrl, useLandingBrandUrl } from "@/data/landing-intake";
import { KineticSlogan } from "@/components/air/KineticSlogan";
import type { Locale } from "@/types";
import { introHeroStyles as styles } from "@/styles/classes";

interface AirIntroHeroProps {
  locale: Locale;
  onLocaleChange(): void;
}

export function AirIntroHero({ locale }: AirIntroHeroProps) {
  const pageContent = content[locale];
  const brandUrl = useLandingBrandUrl();
  const [inviteOpen, setInviteOpen] = useState(false);
  const active = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending.current) return;
    pending.current = true;
    try {
      if (getLandingIdentity().kind === "loading") await bootstrapAuthSession();
      if (!active.current) return;
      // 已登录直接进工作台；访客先拿到邀请码，再在弹窗里选择注册或登录。
      if (getLandingIdentity().kind === "authenticated") {
        navigateToApp(buildAppLoginUrl(true, new URL(window.location.href)));
        return;
      }
      setInviteOpen(true);
    } finally { pending.current = false; }
  }


  return (
    <>
      <section className={styles.airHero} id="top" data-intro-complete="true">
        <div className={styles.heroInner}>
          <header className={styles.heroCopy}>
            <KineticSlogan slogan={pageContent.hero.slogan} locale={locale} />
            <p>{pageContent.hero.description}</p>
            <form className={styles.brandInput} onSubmit={submit} noValidate>
              <Globe2 aria-hidden="true" />
              <label><span className={styles.srOnly}>{pageContent.hero.brandUrlLabel}</span><input value={brandUrl} onChange={(event) => setLandingBrandUrl(event.target.value)} placeholder={pageContent.hero.brandUrlPlaceholder} inputMode="url" /></label>
              <button type="submit">{pageContent.hero.primaryCta}<ArrowRight /></button>
            </form>
            <div className={styles.shortcutRow} aria-label={locale === "en" ? "Agent shortcuts" : "Agent 快捷入口"}>
              {(locale === "en" ? ["Find relevant videos", "Decode the pattern", "Build an original script", "Match the right creators"] : ["寻找相关视频", "拆解有效模式", "构建原创脚本", "匹配合适创作者"]).map((shortcut) => <span key={shortcut}><Sparkles />{shortcut}</span>)}
            </div>
            <div className={styles.watchLink}><ChevronDown aria-hidden="true" />{pageContent.hero.secondaryCta}</div>
          </header>

        </div>
      </section>
      {inviteOpen ? (
        <InviteCodeDialog
          copy={pageContent.hero.invite}
          locale={locale}
          onClose={() => setInviteOpen(false)}
        />
      ) : null}
    </>
  );
}

export type { AirIntroHeroProps };
